/** @jsxImportSource @emotion/react */
import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import {
  FormProvider,
  useController,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { routes } from "../../appRoutingHelpers";
import { RootState } from "../../store";
import * as shopSelectors from "./shopSelectors";
import * as venueSelectors from "../venue/venueSelectors";
import * as venueActions from "../venue/venueActions";
import { SpinnerBlock } from "../common/Spinner";
import useUpdateFormErrors from "../common/hook-form/useUpdateFormErrors";
import VenueShopNav from "../venue/VenueShopNav";
import { OrganizationKeyName } from "../../model/Organization";
import _ from "lodash";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { FormRow } from "../common/hook-form/index";
import { AddDeliveryProviderInput } from "./AddDeliveryProviderInput";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { useGetCountries, useGetDeliveryGroups } from "../../service/api";
import { DragAndDropContainer } from "../../components/DragAndDropContainer";
import { css } from "@emotion/react";
import { ErrorMessage } from "@hookform/error-message";
import { DeliveryProviderRow } from "./DeliveryProviderRow";
import { useGetMenus } from "../menus/service/menus-api";
import { AnyObject } from "yup/lib/types";
import { CheckboxInput } from "../../components/forms/inputs/CheckboxInput";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { useCreateShop, useGetShop, useUpdateShop } from "./service/shop-api";
import { ActionSpinner } from "../../components/ActionSpinner";
import { toastr } from "react-redux-toastr";
import { getCountrySpecificValues } from "../../util";
import { CaptureOn, CollectionMethod } from "./model/ShopSuccessResponse";
import { CheckboxField } from "../../components/forms/fields/CheckboxField";
import { useListKioskConfigurations } from "../kiosk-configurations/service/kiosk-configuration-api";
import {
  FileUploadFormat,
  FileUploadInput,
} from "../../components/forms/inputs/FileUploadInput";
import { shopHttp } from "./service/shop-http";
import { BaseUploadPreview } from "../../components/BaseUploadPreview";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { ButtonAction } from "../../components/NewButtons";
import { Tabs } from "../../components/Tabs";
import { SelectLocaleInput } from "../../components/forms/inputs/SelectLocaleInput";
import {
  AddressPartsInputs,
  addressPartsSchema,
} from "../../components/forms/inputs/AddressPartsInputs";
import {
  ADDRESS_PART_FIELDS,
  addressPartsFromResource,
} from "../../model/Address";

const dndContainerStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

const priorityHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

const orderOptionsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const bodyWrapperStyle = css`
  display: flex;
  gap: 16px;
  flex-direction: column;
  width: 100%;
`;

const previewInnerContainerStyle = css`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

const uploadContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface Props {
  shopId?: string;
  venueId?: string;
  onSubmit?: (
    organization: OrganizationKeyName,
    data: any,
    shopId: string | undefined
  ) => void;
  showOnlyBaseFields?: boolean;
  redirectToProductsOnSave?: boolean;
}

export interface VenueUpsertFormRefHandle {
  isValid: () => Promise<boolean>;
  submit: () => Promise<any>;
}

// The shop GET reads these from the linked venue; the backend refuses them for a shop without one.
const VENUE_OWNED_FIELDS = [
  "description",
  "benefitTagline",
  "benefitDescription",
  "externalLinkTitle",
  "externalLinkUrl",
  "internalNote",
  "logoUrl",
  "radius",
  "isDisabled",
  "isHidden",
  "exposeLocation",
  "showMemberCard",
  "showPersonalCode",
  "eventDate",
  "tags",
  "openingHours",
  "specialOpeningHours",
  "slug",
  "visiblePlatforms",
];

const testDeliveryConfiguration = (
  val: any,
  ctx: yup.TestContext<AnyObject>,
  provider: string
) => {
  const deliveryProviders = ctx.parent.shopDeliveryProviderConfigurations;
  const deliveryProvider = deliveryProviders.find(
    (d: any) => d.provider === provider
  );
  if (!val && deliveryProvider) {
    return false;
  }

  return true;
};

const shopSchema = yup.object({
  ...addressPartsSchema,
  loyaltyMode: yup
    .string()
    .oneOf(["disabled", "enabled_users_only", "enabled_all"]),
  onlinePosFirmaId: yup
    .string()
    .nullable()
    .test(
      "testing if both company id and configuration id are set or none of them",
      "Enter OnlinePOS company id to continue. If OnlinePOS configuration id is set, you need to set OnlinePOS company id as well.",
      (val, ctx) => {
        if (ctx.parent.onlinePosConfigurationId && !val) {
          return false;
        }
        return true;
      }
    ),
  onlinePosConfigurationId: yup.string().nullable(),
  defaultPreparationTime: yup
    .mixed()
    .test("Testing if value is above zero", "Must be above 0", (val) => {
      if (!val) {
        return true;
      }
      return +val >= 0;
    }),
  deliveryRuleGroupId: yup
    .string()
    .nullable()
    .when("collectionMethodCourierEnabled", {
      is: true,
      then: yup
        .string()
        .test(
          "testing if delivery rule group is set",
          "Set a delivery rule group to enable delivery for the shop",
          (val) => {
            return !!val;
          }
        ),
    }),
  jetCollectPointId: yup
    .string()
    .nullable()
    .test(
      "Testing if defined if JET delivery provider is chosen",
      "You have chosen Just Eat as delivery provider. Enter Just Eat collect point id to continue.",
      (val, ctx) => {
        return testDeliveryConfiguration(val, ctx, "jet_daas");
      }
    ),
  foodoraGoOutletId: yup
    .string()
    .nullable()
    .test(
      "Testing if defined if Foodora Go delivery provider is chosen",
      "You have chosen Foodora Go as delivery provider. Enter Foodora Go outlet id to continue.",
      (val, ctx) => {
        return testDeliveryConfiguration(val, ctx, "foodora_go");
      }
    ),
  woltDriveVenueId: yup
    .string()
    .nullable()
    .test(
      "Testing if defined if Wolt Drive delivery provider is chosen",
      "You have chosen Wolt Drive as delivery provider. Enter Wolt Drive venue id to continue.",
      (val, ctx) => {
        return testDeliveryConfiguration(val, ctx, "woltdrive");
      }
    ),
  deliveryInstructions: yup.string().when("collectionMethodCourierEnabled", {
    is: true,
    then: yup
      .string()
      .test(
        "testing if delivery isntrutions are set",
        "Enter delivery courier instructions to continue",
        (val) => {
          return !!val;
        }
      ),
    otherwise: yup.string().nullable(),
  }),
  shopDeliveryProviderConfigurations: yup
    .array()
    .when("collectionMethodCourierEnabled", {
      is: true,
      then: yup
        .array()
        .test(
          "Testing if delivery is chosen as option",
          "Set at least one delivery provider to enable delivery for the shop",
          (val) => {
            return !!val && val.length > 0;
          }
        ),
      otherwise: yup.array().nullable(),
    }),
});

const ShopUpsert = React.forwardRef<VenueUpsertFormRefHandle, Props>(
  (props, ref) => {
    const { locale } = getCountrySpecificValues();

    const dispatch = useDispatch();
    const location = useLocation();

    const formMethods = useForm({
      defaultValues: {
        shouldStartManufacturingOnAccept: true,
        shouldSendTextMessages: false,
        onlinePosConfigurationId: "",
        onlinePosFirmaId: "",
        locale: locale || "da",
        autoReadyCollectionMethods: [],
        autoDeliverCollectionMethods: [],
        shopDeliveryProviderConfigurations: [],
        captureOn: CaptureOn.READY,
        loyaltyMode: "enabled_all",
        addressLine: "",
        addressLine2: "",
        postcode: "",
        city: "",
      } as any,
      resolver: yupResolver(shopSchema),
    });
    const { handleSubmit, setError } = formMethods;
    const params = useParams<{ id?: string; venueId?: string }>();

    const shopId = props.shopId || params.id;
    const venueId = props.venueId || params.venueId;

    const isEdit = !!shopId;
    const isStandaloneView = !props.venueId && !props.shopId;
    const isAttachedToVenue = !!venueId;
    // Linking the venue on create gives the shop the venue's whole address.
    const takesAddressFromVenue = isAttachedToVenue && !isEdit;
    const navigate = useNavigate();
    const organization =
      useSelector(authSelectors.selectedOrganizationKeyName) || "";

    const {
      field: {
        onChange: onChangeAutoReadyCollectionMethods,
        ...restAutoReadyCollectionMethods
      },
    } = useController({
      control: formMethods.control,
      name: "autoReadyCollectionMethods",
    });

    const {
      field: {
        onChange: onChangeAutoDeliverCollectionMethods,
        ...restAutoDeliverCollectionMethods
      },
    } = useController({
      control: formMethods.control,
      name: "autoDeliverCollectionMethods",
    });

    const handleAutoDeliverChange = (
      method: CollectionMethod,
      type: "add" | "remove"
    ) => {
      if (type === "add") {
        onChangeAutoDeliverCollectionMethods([
          ...restAutoDeliverCollectionMethods.value,
          method,
        ]);
      } else {
        onChangeAutoDeliverCollectionMethods(
          restAutoDeliverCollectionMethods.value.filter(
            (m: CollectionMethod) => m !== method
          )
        );
      }
    };

    const handleAutoReadyChange = (
      method: CollectionMethod,
      type: "add" | "remove"
    ) => {
      if (type === "add") {
        onChangeAutoReadyCollectionMethods([
          ...restAutoReadyCollectionMethods.value,
          method,
        ]);
      } else {
        onChangeAutoReadyCollectionMethods(
          restAutoReadyCollectionMethods.value.filter(
            (m: CollectionMethod) => m !== method
          )
        );
      }
    };

    const { data: countries, error: listCountriesError } = useGetCountries();

    const countryOptions = React.useMemo(
      () =>
        (countries || []).map((o) => ({
          label: o.englishName,
          value: o.countryCode,
        })),
      [countries]
    );

    const {
      data: menus,
      isLoading: menusAreLoading,
      error: listMenusError,
    } = useGetMenus(organization);

    const {
      data: shop,
      isLoading: isLoadingShop,
      error: getShopError,
    } = useGetShop(organization, shopId);

    const {
      mutateAsync: createShop,
      isLoading: creatingShopIsLoading,
    } = useCreateShop();

    const {
      mutateAsync: updateShop,
      isLoading: updatingShopIsLoading,
    } = useUpdateShop();

    const { fields, remove, append, update } = useFieldArray({
      control: formMethods.control,
      name: "shopDeliveryProviderConfigurations",
    });

    const selectableMenus = menus?.map((d) => ({
      value: d.id,
      label: d.name,
    }));

    const {
      data: deliveryRuleGroups,
      isLoading: deliveryGroupsLoading,
      error: listDeliveryRuleGroupsError,
    } = useGetDeliveryGroups(organization);

    const {
      data: shopConfigurations,
      isLoading: isLoadingShopConfigurations,
      error: listShopConfigurationsError,
    } = useListKioskConfigurations(organization);

    const selectableDeliveryRuleGroups = deliveryRuleGroups?.map((d) => ({
      value: d.id,
      label: d.name,
    }));

    selectableMenus?.unshift({
      value: "",
      label: "",
    });

    const selectedOrganizationKeyName = useSelector(
      authSelectors.selectedOrganizationKeyName
    );

    const errors = useSelector((state: RootState) =>
      shopSelectors.upsertShopErrors(state, shopId)
    );
    const venue = useSelector(venueSelectors.venueForEdit);
    const isLoadingVenue = useSelector(venueSelectors.isLoadingVenue);

    useUpdateFormErrors(errors, setError);

    React.useEffect(() => {
      if (!venueId) {
        return;
      }
      const isVenueAlreadyLoaded = !!venue;
      if (isVenueAlreadyLoaded) {
        return;
      }

      dispatch(venueActions.getVenueForEdit(organization, venueId));
    }, [dispatch, organization, venueId, venue]);

    const onSubmit = async ({ files, ...data }: any & { files: File[] }) => {
      if (files && files[0] && shopId) {
        const allergenesUrl = await shopHttp.uploadAllergensPdf({
          file: files[0],
          organizationKeyName: organization,
          id: shopId,
        });

        data = { ...data, allergenesUrl };
      }

      if (data.allergenesUrlDirect) {
        data.allergenesUrl = data.allergenesUrlDirect;
      }

      const { allergenesUrlDirect, ...restData } = data;
      data = restData;

      if (data.deliveryProviderConfigurationId === "") {
        data.deliveryProviderConfigurationId = null;
      }
      if (data.deliveryRuleGroupId === "") {
        data.deliveryRuleGroupId = null;
      }
      if (data.maxDeliveryDistance === 0) {
        data.maxDeliveryDistance = null;
      }
      if (data.menuId === "") {
        data.menuId = null;
      }
      if (data.tableStrategy === "") {
        data.tableStrategy = null;
      }
      if (data.buzzerStrategy === "") {
        data.buzzerStrategy = null;
      }
      if (data.defaultPreparationTime === "") {
        data.defaultPreparationTime = null;
      }
      if (data.displayIdNamespace === "") {
        data.displayIdNamespace = null;
      }
      if (data.onlinePosFirmaId === "") {
        data.onlinePosFirmaId = null;
      }
      if (data.woltDriveVenueId === "") {
        data.woltDriveVenueId = null;
      }
      if (data.foodoraGoOutletId === "") {
        data.foodoraGoOutletId = null;
      }
      if (data.jetCollectPointId === "") {
        data.jetCollectPointId = null;
      }
      if (data.phoneNumber === "") {
        data.phoneNumber = null;
      }
      if (data.deliveryInstructions === "") {
        data.deliveryInstructions = null;
      }
      data.acceptedPaymentTypes = null;

      data.shouldStartManufacturingOnAccept = true;

      // Not sending delivery options to backend
      // TODO: remove after some time
      const { deliveryOptions, ...rest } = data;
      data = rest;

      data = _.omit(data, VENUE_OWNED_FIELDS);

      // The GET's nested read shape; the request takes the parts top-level.
      delete data.address;
      if (takesAddressFromVenue) {
        data = _.omit(data, ADDRESS_PART_FIELDS);
      }

      if (venueId) {
        data = {
          ...data,
          venueId,
        };
      }
      let _shopId = shopId;
      try {
        if (isEdit && _shopId) {
          await updateShop({
            organizationKeyName: organization,
            id: _shopId,
            data,
          });
        } else {
          const {
            name,
            title,
            venueId,
            countryCode,
            captureOn,
            ...rest
          } = data;
          const type = "physical";
          // Creating shop with basic info
          _shopId = await createShop({
            organizationKeyName: organization,
            data: {
              name,
              title,
              venueId,
              countryCode,
              type,
              captureOn,
              paymentProviders: null,
            },
          });
          // Updating shop with remaining info
          await updateShop({
            organizationKeyName: organization,
            id: _shopId,
            data: {
              name,
              title,
              venueId,
              captureOn,
              type,
              ...rest,
            },
          });
        }
        if ((data.venueId || props.redirectToProductsOnSave) && _shopId) {
          navigate(routes.shopProductListForShop(organization, _shopId));
        } else {
          navigate(routes.shopList(organization));
        }
        toastr.success(
          "Success",
          `Shop ${isEdit ? "updated" : "created"} successfully`
        );
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error);
        toastr.error("Error", errorMessage);
      }
    };

    const reset = formMethods.reset;

    React.useEffect(
      function resetFormWhenEditng() {
        if (isEdit && shop) {
          reset({
            ...shop,
            ...addressPartsFromResource(shop.address),
            locale: shop.locale || locale || "da",
            tableStrategy: shop.tableStrategy || "",
            buzzerStrategy: shop.buzzerStrategy || "",
            allergenesUrl: shop.allergenesUrl,
            allergenesUrlDirect: shop.allergenesUrl,
          });
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isEdit, shop, reset]
    );

    React.useEffect(
      function resetFormWhenCreatingNewWithValsFromVenue() {
        if (isEdit || !venue) {
          return;
        }
        formMethods.setValue("countryCode", venue.countryCode);
        formMethods.setValue("title", venue.title);
        formMethods.setValue("name", _.kebabCase(_.lowerCase(venue.title)));
      },
      [isEdit, venue, formMethods]
    );

    useHandleFetchError(listCountriesError);
    useHandleFetchError(listMenusError);
    useHandleFetchError(getShopError);
    useHandleFetchError(listDeliveryRuleGroupsError);
    useHandleFetchError(listShopConfigurationsError);

    const autoReadyAndDeliverConfigOptions = [
      {
        readyLabel:
          "Automatically mark pick-up orders as ready when time has surpassed customer pick up time",
        deliverLabel:
          "Automatically mark pick-up orders as picked up when time has surpassed customer pick up time",
        value: CollectionMethod.PICKUP,
      },
      {
        readyLabel:
          "Automatically mark delivery orders as ready for courier pick up when time has surpassed courier pick up time",
        /* deliverLabel:
          "Automatically mark delivery orders as delivered when time has surpassed delivery time", */
        value: CollectionMethod.COURIER,
      },
      {
        readyLabel:
          "Automatically mark eat-in orders as ready for customer pick up / table delivery when time has surpassed ready time",
        deliverLabel:
          "Automatically mark eat-in orders as delivered to table / picked up by customer when time has surpassed ready time",
        value: CollectionMethod.EAT_IN,
      },
    ];

    if (
      isLoadingVenue ||
      isLoadingShop ||
      deliveryGroupsLoading ||
      menusAreLoading ||
      isLoadingShopConfigurations
    ) {
      return <SpinnerBlock />;
    }

    return (
      <>
        <FormProvider {...formMethods}>
          <div key={location.key} className="container mt-5">
            {!props.showOnlyBaseFields && (
              <>
                <GoBackLinkButton
                  to={routes.shopList(selectedOrganizationKeyName)}
                  text="Back to all shops"
                />
                {!isStandaloneView && (
                  <VenueShopNav
                    organization={organization}
                    shopId={shop?.id}
                    venueId={venueId || shop?.venueId}
                  />
                )}
              </>
            )}

            <div className="row">
              <div className="col-12">
                <form className="card" onSubmit={handleSubmit(onSubmit)}>
                  {venueId && (
                    <div className="card-header">
                      <h3 className="card-title">
                        {venue ? (
                          <>To add menu, you need to create shop first</>
                        ) : (
                          <>{isEdit ? <>Shop Edit</> : <>New Shop</>}</>
                        )}
                      </h3>
                    </div>
                  )}
                  <div className="card-body" css={bodyWrapperStyle}>
                    {shop?.title && (
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextSemibold mb-2"
                      >
                        {shop?.title}
                      </StyledText>
                    )}
                    {!isAttachedToVenue && !props.showOnlyBaseFields && (
                      <>
                        <TextInput label="Name" name="name" />
                        <TextInput label="Title" name="title" />
                        <SelectInput
                          label="Country"
                          name="countryCode"
                          options={countryOptions}
                        />
                      </>
                    )}
                    <div className="mt-0">
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyHeaderOneSemi mb-2"
                      >
                        Standard Restaurant configuration
                      </StyledText>
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-1"
                      >
                        Configure the standard restaurant settings below. Before
                        you can add products to the menu, you need to configure
                        the standard restaurant settings.
                      </StyledText>
                    </div>
                    <div css={orderOptionsStyle}>
                      <StyledText
                        className="bodySubtextSemibold mb-0"
                        color={colors.textPrimary}
                      >
                        Order options
                      </StyledText>
                      <FormRow>
                        <CheckboxInput
                          name="collectionMethodPickupEnabled"
                          label="Pick up"
                        />
                        <CheckboxInput
                          name="collectionMethodCourierEnabled"
                          label="Delivery"
                        />
                        <CheckboxInput
                          name="collectionMethodEatInEnabled"
                          label="Eat-in"
                        />
                      </FormRow>
                      <StyledText
                        className="bodySubtextRegular mb-0"
                        color={colors.textSecondary}
                      >
                        Choose if the restaurant accepts pick up orders,
                        delivery orders and eat-in orders. If your restaurant
                        accepts delivery orders, please fill out the delivery
                        instructions aswell.
                      </StyledText>
                    </div>
                    <FormRow>
                      <TextInput
                        label="VAT Number"
                        name="vatNumber"
                        helperText="This is the VAT number of the restaurant. If you have one VAT number for every restaurant then put that here. If you have different VAT numbers for each of your restaurant you should enter the one connected to this restaurant."
                      />
                      <TextInput
                        label="Phone number"
                        name="phoneNumber"
                        helperText="Enter the phonenumber of the restaurant."
                      />
                    </FormRow>
                    {!takesAddressFromVenue && <AddressPartsInputs />}
                    <FormRow>
                      <TextInput
                        label="Table booking url"
                        name="tableBookingUrl"
                        helperText="If you have an external table booking provider you can enter the link here, and your customers can access the table booking page from the app and website."
                      />
                      <TextInput
                        label="OnlinePos Firma id"
                        name="onlinePosFirmaId"
                        helperText="Firma id for venue in OnlinePos used for reporting"
                      />
                      <SelectInput
                        label="Delivery rule group"
                        name="deliveryRuleGroupId"
                        options={selectableDeliveryRuleGroups || []}
                        helperText="Choose what delivery rule group that applies for this restaurant."
                      />
                    </FormRow>

                    <StyledText className="bodySubtextSemibold mb-2">
                      Allergens document
                    </StyledText>
                    <Tabs
                      tabs={["Upload", "Link"]}
                      tabPanels={[
                        <div css={uploadContainerStyle}>
                          {formMethods.watch("allergenesUrl") && (
                            <div css={previewInnerContainerStyle}>
                              <BaseUploadPreview
                                type="pdf"
                                url={formMethods.watch("allergenesUrl")}
                              />
                            </div>
                          )}
                          <FileUploadInput
                            onDrop={() =>
                              formMethods.setValue("allergenesUrlDirect", "")
                            }
                            accept={FileUploadFormat.PDF}
                          />
                        </div>,
                        <FormRow>
                          <TextInput
                            name="allergenesUrlDirect"
                            onChange={() => {
                              formMethods.setValue("allergenesUrl", "");
                              formMethods.setValue("files", undefined);
                            }}
                            helperText="The URL where your allergens document is located"
                          />
                        </FormRow>,
                      ]}
                    />

                    <FormRow>
                      <TextInput
                        label="Smiley report URL"
                        name="smileyUrl"
                        helperText="The URL where your smiley report is located"
                      />
                      <TextInput
                        label="Max delivery distance"
                        name="maxDeliveryDistance"
                        helperText="The maximum delivery distance from the restaurant in meters."
                      />
                    </FormRow>
                    <FormRow>
                      <TextInput
                        label="OnlinePos HeapsGo configuration id"
                        name="onlinePosConfigurationId"
                        helperText="HeapsGo id of the OnlinePos configuration for sending orders to OnlinePos. Leave empty and the HeapsGo team configure if in doubt."
                      />
                      <TextInput
                        label="Wolt Drive venue id"
                        name="woltDriveVenueId"
                        helperText="The venue id for doing deliveries with Wolt Drive. Only relevant if you have Wolt drive as delivery partner."
                      />
                      <TextInput
                        label="Delivery instructions"
                        name="deliveryInstructions"
                        helperText="Instructions we will send to currier about your restaurant. This could fx be about where they should pick up the order they are to pick up or where they should wait for you to give them the order."
                      />
                    </FormRow>
                    <FormRow>
                      <TextInput
                        label="FoodoraGo outlet id"
                        name="foodoraGoOutletId"
                        helperText="The ID that enables the Foodora Go integration. Will be supplied by Foodora. Get in contact with HeapsGo support if in doubt."
                      />
                      <TextInput
                        label="Just Eat collect point id"
                        name="jetCollectPointId"
                        helperText="The ID that enables the Just Eat delivery integration. Will be supplied by Just Eat. Get in contact with HeapsGo support if in doubt."
                      />
                      <TextInput
                        label="Default preparation time"
                        name="defaultPreparationTime"
                        type="number"
                        helperText="How long time on average an order takes to make in seconds. HeapsGo automatically add +/- 5 minutes, so 15 minutes becomes 10-20 minutes for the user."
                      />
                    </FormRow>
                    <CheckboxInput
                      label="Auto accept pickup as pre-order. If enabled, pickup orders will be accepted automatically as pre-orders"
                      name="autoAcceptPickupAsPreOrder"
                    />
                    <CheckboxInput
                      label="Auto accept delivery as pre-order. If enabled, delivery orders will be accepted automatically as pre-orders"
                      name="autoAcceptDeliveryAsPreOrder"
                    />
                    <CheckboxInput
                      label="Enable capacity control. If enabled, the shop will take orders based on capacity control timeslots"
                      name="enableCapacityControl"
                    />
                    {/* <SwitchInput
                    label="Should a text message be sent with order tracking link? If enabled, users will receive a text message with a order tracking link."
                    name="shouldSendTextMessages"
                  /> */}
                    <AddDeliveryProviderInput
                      organization={organization}
                      currentProviderIds={formMethods
                        .watch("shopDeliveryProviderConfigurations")
                        ?.map((a: any) => a.configurationId)}
                      addShopDeliveryProviderConfiguration={(value: any) => {
                        append(value);
                      }}
                    />
                    <div css={priorityHeaderStyle}>
                      <StyledText
                        color={colors.textPrimary}
                        className="bodySubtextSemibold mb-0"
                      >
                        Delivery provider priority
                      </StyledText>
                      <StyledText
                        color={colors.textSecondary}
                        className="bodySubtextRegular mb-0"
                      >
                        Drag and drop to change priority
                      </StyledText>
                    </div>
                    <DragAndDropContainer
                      containerStyle={dndContainerStyle}
                      onEnd={(_, containerRef) => {
                        if (!containerRef) {
                          return;
                        }
                        // iterating over dom children. Each of them has the original index as data attribute
                        // @ts-expect-error
                        [...containerRef.current?.children].forEach(
                          (b, index) => {
                            // Index to update in provider array.
                            const providerIndex = b.dataset.id;
                            update(providerIndex, {
                              ...formMethods.watch(
                                `shopDeliveryProviderConfigurations.${providerIndex}`
                              ),
                              priority: index + 1,
                            });
                          }
                        );
                      }}
                    >
                      <>
                        {fields
                          .map((p: any, index: number) => ({
                            ...p,
                            fieldIndex: index,
                          }))
                          .sort(
                            (a: any, b: any) =>
                              Number(a.priority) - Number(b.priority)
                          )
                          ?.map((p: any) => {
                            return (
                              <DeliveryProviderRow
                                key={p.fieldIndex}
                                provider={p}
                                remove={remove}
                              />
                            );
                          })}
                      </>
                    </DragAndDropContainer>
                    <ErrorMessage
                      errors={formMethods.formState.errors}
                      name="shopDeliveryProviderConfigurations"
                      render={({ message }) => (
                        <StyledText
                          className="bodySubtextRegular mb-0"
                          color={colors.errorDark}
                        >
                          {message}
                        </StyledText>
                      )}
                    />
                    <div className="mt-2">
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyHeaderOneSemi mb-2"
                      >
                        Advanced Restaurant configuration
                      </StyledText>
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-1"
                      >
                        Settings that are not required to be filled out.
                      </StyledText>
                    </div>
                    <FormRow>
                      <SelectInput
                        label="Self service kiosk configuration id"
                        name="selfServiceConfigId"
                        options={[
                          {
                            label: "none",
                            value: "",
                          },
                          ...(shopConfigurations || []).map((config) => ({
                            label: config.name,
                            value: config.id,
                          })),
                        ]}
                        helperText="Choose if the user is required to input table they sit at, their name or other additional eat-in information when placing an eat-in order. Contact HeapsGo support to customize communication in the app, web and kiosk."
                      />
                      <TextInput
                        label="Display id namespace"
                        name="displayIdNamespace"
                        helperText="The key that is used when generating handout ids. E.g. if multiple shops are placed in the same location, they should have the same key to use the same sequence."
                      />
                    </FormRow>
                    <FormRow>
                      <SelectInput
                        label="Eat-in user input"
                        name="tableStrategy"
                        options={[
                          {
                            label: "User input is not required",
                            value: "",
                          },
                          {
                            label: "User input is required",
                            value: "manual_from_table",
                          },
                        ]}
                        helperText="Choose if the user is required to input table they sit at, their name or other additional eat-in information when placing an eat-in order. Contact HeapsGo support to customize communication in the app, web and kiosk."
                      />
                      <SelectInput
                        label="Buzzer number user input"
                        name="buzzerStrategy"
                        options={[
                          {
                            label: "User input is not required",
                            value: "",
                          },
                          {
                            label: "User input is required",
                            value: "manual_from_buzzer",
                          },
                        ]}
                        helperText="Choose if the user is required to input the identification number of a physical buzzer when ordering in your restaurant. We refer to them as buzzers. Tip: This is usually relevant for customers who use the HeapsGo kiosk solution."
                      />
                    </FormRow>
                    <FormRow>
                      <SelectLocaleInput name="locale" />
                      <SelectInput
                        label="When should users be charged for orders?"
                        name="captureOn"
                        options={[
                          {
                            label:
                              "When the order is marked ready by restaurant staff on the Staff Tablet",
                            value: CaptureOn.READY,
                          },
                          {
                            label:
                              "When the order is delivered to or picked up by the customer",
                            value: CaptureOn.FINALIZE,
                          },
                          {
                            label:
                              "When the order is accepted by restaurant staff on the Staff Tablet",
                            value: CaptureOn.ACCEPT,
                          },
                        ]}
                      />
                    </FormRow>

                    <FormRow>
                      <SelectInput
                        label="Loyalty on webshop"
                        name="loyaltyMode"
                        helperText="Select how you want to handle loyalty on your webshop. Loyalty is always available on apps, so changing this will not affect apps."
                        options={[
                          {
                            label:
                              "Loyalty disabled. Customers will not be able to use vouchers or member cards or enter promotion codes.",
                            value: "disabled",
                          },
                          {
                            label:
                              "Logged in users can use vouchers and member cards and enter promotion codes. Both logged in users and guests can use LifePeaks gift cards.",
                            value: "enabled_all",
                          },
                          {
                            label:
                              "Logged in users can use vouchers and member cards, enter promotion codes, but LifePeaks gift cards cannot be used.",
                            value: "enabled_users_only",
                          },
                        ]}
                      />
                    </FormRow>
                    <CheckboxInput
                      label="Whether a pin code should be generated for the order and shown in Kiosk receipt."
                      name="generatePinCode"
                    />
                    <>
                      {autoReadyAndDeliverConfigOptions.map(
                        ({ readyLabel, deliverLabel, value }) => {
                          return (
                            <React.Fragment key={value}>
                              <CheckboxField
                                label={readyLabel}
                                checked={formMethods
                                  .watch("autoReadyCollectionMethods")
                                  ?.includes(value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    handleAutoReadyChange(value, "add");
                                  } else {
                                    handleAutoReadyChange(value, "remove");
                                  }
                                }}
                              />
                              {deliverLabel && (
                                <CheckboxField
                                  label={deliverLabel}
                                  checked={formMethods
                                    .watch("autoDeliverCollectionMethods")
                                    ?.includes(value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleAutoDeliverChange(value, "add");
                                    } else {
                                      handleAutoDeliverChange(value, "remove");
                                    }
                                  }}
                                />
                              )}
                            </React.Fragment>
                          );
                        }
                      )}
                    </>
                  </div>
                  <div className="card-footer d-flex justify-content-end">
                    <ButtonAction
                      type="submit"
                      disabled={creatingShopIsLoading || updatingShopIsLoading}
                    >
                      {creatingShopIsLoading || updatingShopIsLoading ? (
                        <ActionSpinner />
                      ) : isEdit ? (
                        "Save Changes"
                      ) : (
                        "Create restaurant"
                      )}
                    </ButtonAction>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </FormProvider>
      </>
    );
  }
);

export default ShopUpsert;
